import { notification, Splitter } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useGetMediasToQualify } from '../../../../utils/api/medias/useGetMediasToQualify';
import { useGetMediasToQualifyCountStatus } from '../../../../utils/api/medias/useGetMediasToQualifyCountStatus';
import { useUpdateStatusMediasToQualify } from '../../../../utils/api/medias/useUpdateStatusMediasToQualify';
import { STATUS_CONFIG } from '../../../../utils/fn/medias';
import { useMediaToQualifyFilters } from '../../../../utils/zustand/hooks/useMediaToQualifyFilters';
import FodlersCategories from './FodlersCategories';
import MediaList from './mediaList/index';
import MediaUpdate from './mediaUpdate';
import RejectedReasonPanel from './mediaUpdate/RejectedReasonPannel';
import { useGetAllMediaIds } from '../../../../utils/api/medias/useGetAllMediaIds';
import { MAX_SELECTED_MEDIAS } from '../../../../utils/const/medias';

const STATUS_FOLDER_ORDER = [
  'to_qualify',
  'to_validate',
  'validated',
  'rejected',
  'archived',
];

const buildCategories = (counts) => {
  const countByStatus = (status) => {
    switch (status) {
      case 'to_qualify':
        return counts?.to_qualify_count;
      case 'to_validate':
        return counts?.to_validate_count;
      case 'validated':
        return counts?.validated_count;
      case 'rejected':
        return counts?.rejected_count;
      case 'archived':
        return counts?.archived_count;
      default:
        return counts?.total_count;
    }
  };

  return [
    {
      id: 'all',
      name: 'Tous les médias',
      status: null,
      count: counts?.total_count,
    },
    ...STATUS_FOLDER_ORDER.map((status) => ({
      id: status,
      name: STATUS_CONFIG[status]?.label ?? status,
      status,
      count: countByStatus(status),
    })),
  ];
};

const filterMinMax = (filter) => {
  const filterList = filter?.split('-') || [];
  const minValue = parseInt(filterList[0]) || null;
  const maxValue = parseInt(filterList[1]) || null;

  return {
    min: minValue,
    max: maxValue,
  };
};

const filterMinMaxPeriod = (filter) => {
  let minDate = null;
  switch (filter) {
    case 'today': {
      minDate = new Date();
      break;
    }
    case 'week': {
      minDate = new Date();
      const day = minDate.getDay();
      minDate.setDate(minDate.getDate() + (day === 0 ? -6 : 1 - day));
      break;
    }
    case 'month': {
      minDate = new Date();
      minDate.setDate(1);
      break;
    }
    case 'year': {
      minDate = new Date();
      minDate.setMonth(0);
      minDate.setDate(1);
      break;
    }
  }

  const minValue = minDate
    ? [
        minDate.getFullYear(),
        String(minDate.getMonth() + 1).padStart(2, '0'),
        String(minDate.getDate()).padStart(2, '0'),
      ].join('-')
    : null;

  return {
    min: minValue,
    max: null,
  };
};

const MediaQualification = () => {
  const [viewMode, setViewMode] = useState('large');

  const { filters, setSelectedStatusIdFilter } = useMediaToQualifyFilters();

  const filterSizes = useMemo(() => {
    return filterMinMax(filters.selectedSizes);
  }, [filters.selectedSizes]);

  const filterResolutions = useMemo(() => {
    return filterMinMax(filters.selectedResolutions);
  }, [filters.selectedResolutions]);

  const filterCreatedAt = useMemo(() => {
    return filterMinMaxPeriod(filters.selectedCreatedAt);
  }, [filters.selectedCreatedAt]);

  const [searchMedia, onSearchMediaChange] = useState('');

  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const { getAllMediaIds } = useGetAllMediaIds();
  const selectAllRequestId = useRef(0);

  useEffect(() => {
    selectAllRequestId.current += 1;
  }, [searchMedia, filters]);

  const selectAllMedias = async () => {
    const requestId = ++selectAllRequestId.current;

    try {
      const data = await getAllMediaIds({
        limit: MAX_SELECTED_MEDIAS,
        search: searchMedia,
        status:
          isAllFolder || isArchivedFolder ? null : filters.selectedStatusId,
        is_archived: isArchivedFolder ? true : isAllFolder ? undefined : false,
        type: filters.selectedType === 'all' ? null : filters.selectedType,
        has_all_tags:
          filters.selectedTags === 'all'
            ? null
            : Array.isArray(filters.selectedTags)
              ? filters.selectedTags.join(', ')
              : filters.selectedTags,
        media_format: filters.selectedMediaFormat,
        image_order: filters.selectedOrder,
        product__category__name: filters.selectedCategoryName,
        min_size: filterSizes.min,
        max_size: filterSizes.max,
        min_resolution: filterResolutions.min,
        max_resolution: filterResolutions.max,
        min_created_at: filterCreatedAt.min,
        max_created_at: filterCreatedAt.max,
        aspect_ratio: filters.selectedDimensions,
        ordering: filters.sortBy,
      });

      if (requestId === selectAllRequestId.current) {
        setSelectedMediaIds((previousIds) => {
          const mergedIds = [
            ...new Set([...previousIds, ...(data?.ids ?? [])]),
          ];
          return mergedIds.slice(0, MAX_SELECTED_MEDIAS);
        });
      }
    } catch (e) {
      if (requestId !== selectAllRequestId.current) return;
      notification.error({
        message: 'Erreur',
        description: `Impossible de sélectionner les médias : ${e}`,
        placement: 'bottomRight',
        duration: 5,
      });
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { counts } = useGetMediasToQualifyCountStatus({
    search: searchMedia,
    type: filters.selectedType === 'all' ? null : filters.selectedType,
    has_all_tags:
      filters.selectedTags === 'all'
        ? null
        : Array.isArray(filters.selectedTags)
          ? filters.selectedTags.join(', ')
          : filters.selectedTags,
    media_format: filters.selectedMediaFormat,
    image_order: filters.selectedOrder,
    product__category__name: filters.selectedCategoryName,
    aspect_ratio: filters.selectedDimensions,
  });

  const isArchivedFolder = filters.selectedStatusId === 'archived';
  const isAllFolder = filters.selectedStatusId === 'all';

  const { medias, isLoading, count } = useGetMediasToQualify({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    search: searchMedia,
    status: isAllFolder || isArchivedFolder ? null : filters.selectedStatusId,
    is_archived: isArchivedFolder ? true : isAllFolder ? undefined : false,
    type: filters.selectedType === 'all' ? null : filters.selectedType,
    has_all_tags:
      filters.selectedTags === 'all'
        ? null
        : Array.isArray(filters.selectedTags)
          ? filters.selectedTags.join(', ')
          : filters.selectedTags,
    media_format: filters.selectedMediaFormat,
    image_order: filters.selectedOrder,
    product__category__name: filters.selectedCategoryName,
    min_size: filterSizes.min,
    max_size: filterSizes.max,
    min_resolution: filterResolutions.min,
    max_resolution: filterResolutions.max,
    min_created_at: filterCreatedAt.min,
    max_created_at: filterCreatedAt.max,
    aspect_ratio: filters.selectedDimensions,
    ordering: filters.sortBy,
  });

  const [hasRejectedMedias, setHasRejectedMedias] = useState(false);
  const { updateStatusMediasToQualify } = useUpdateStatusMediasToQualify();

  const categories = useMemo(() => buildCategories(counts), [counts]);

  const selectedCategory = useMemo(
    () =>
      categories.find((c) => c.id === filters.selectedStatusId) ??
      categories[0],
    [categories, filters.selectedStatusId]
  );

  const filteredMedias = useMemo(() => {
    const status = selectedCategory?.status;
    if (status == null) return medias;
    if (selectedCategory?.id === 'archived') {
      return medias?.filter((m) => m.is_archived === true);
    }
    return medias?.filter((m) => m.status === status && m.is_archived !== true);
  }, [medias, selectedCategory]);

  const selectedMedias = useMemo(
    () =>
      selectedMediaIds
        .map((id) => medias?.find((m) => m.id === id) || { id })
        .filter(Boolean),
    [medias, selectedMediaIds]
  );

  const handleCategorySelect = useCallback(
    (category) => {
      setSelectedStatusIdFilter(category.id);
    },
    [setSelectedStatusIdFilter]
  );

  const handleMediaToggle = useCallback((mediaId) => {
    selectAllRequestId.current += 1;
    setSelectedMediaIds((prev) =>
      prev.includes(mediaId)
        ? prev.filter((id) => id !== mediaId)
        : [...prev, mediaId]
    );
  }, []);

  const handleBulkToggle = useCallback((mediaIds) => {
    selectAllRequestId.current += 1;
    setSelectedMediaIds(mediaIds);
  }, []);

  const handleValidateMedia = useCallback(
    async (id) => {
      try {
        const result = await updateStatusMediasToQualify({
          status: 'validated',
          mediaIds: [id],
        });

        if (result?.errors?.length > 0) {
          throw new Error(result.errors.map((e) => e.error).join('; '));
        }

        notification.info({
          message: 'Validation du média',
          description:
            'Le média est en cours de validation. Vous recevrez un mail quand le média sera validé.',
          placement: 'bottomRight',
          duration: 3,
        });
      } catch (e) {
        notification.error({
          message: 'Erreur de validation du média',
          description: `Le média n'a pas pu être validé : ${e}`,
          placement: 'bottomRight',
          duration: 3,
        });
      }
    },
    [updateStatusMediasToQualify]
  );

  const hasSelectedMedia = useMemo(
    () => selectedMedias.length > 0,
    [selectedMedias]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchMedia, filters]);
  console.log('hasRejectedMedias', hasRejectedMedias);
  console.log('selectedMedias', selectedMedias);

  return (
    <div style={{ height: 'calc(100vh - 165px)', padding: '0px' }}>
      <Splitter key={`splitter-${hasSelectedMedia}-${hasRejectedMedias}`}>
        <Splitter.Panel defaultSize={200}>
          <FodlersCategories
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
          />
        </Splitter.Panel>
        <Splitter.Panel>
          <MediaList
            setHasRejectedMedias={setHasRejectedMedias}
            medias={filteredMedias}
            isLoading={isLoading}
            count={count}
            selectedMediaIds={selectedMediaIds}
            searchMedia={searchMedia}
            onSearchMediaChange={onSearchMediaChange}
            onMediaToggle={handleMediaToggle}
            onBulkToggle={handleBulkToggle}
            displayedStatus={selectedCategory?.status ?? null}
            onClearSelection={() => {
              selectAllRequestId.current += 1;
              setSelectedMediaIds([]);
            }}
            onValidateMedia={handleValidateMedia}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            onSelectAllMedias={selectAllMedias}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
        </Splitter.Panel>
        {hasSelectedMedia && !hasRejectedMedias && (
          <Splitter.Panel defaultSize={300}>
            <MediaUpdate medias={selectedMedias} />
          </Splitter.Panel>
        )}
        {hasSelectedMedia && hasRejectedMedias && (
          <Splitter.Panel defaultSize={300}>
            <RejectedReasonPanel
              selectedMedias={selectedMedias}
              setHasRejectedMedias={setHasRejectedMedias}
            />
          </Splitter.Panel>
        )}
      </Splitter>
    </div>
  );
};

export default MediaQualification;
